class PlaayHero extends HTMLElement {
  constructor() {
    super();
    this.activeIndex = 0;
    this.timer = null;
    this.touchStartX = 0;
    this.soundOn = false;
  }

  connectedCallback() {
    if (this.dataset.initialized === 'true') return;
    this.dataset.initialized = 'true';

    const configElement = this.querySelector('[data-plaay-config]');
    this.config = configElement ? JSON.parse(configElement.textContent) : {};

    this.autoplayEnabled = this.dataset.autoplay === 'true';
    this.duration = Number(this.dataset.duration || 7000);
    this.style.setProperty('--plaay-duration', `${this.duration}ms`);

    this.buttons = [...this.querySelectorAll('[data-plaay-chapter]')];
    this.slides = [...this.querySelectorAll('[data-plaay-slide]')];
    this.content = this.querySelector('[data-plaay-content]');
    this.eyebrow = this.querySelector('[data-plaay-eyebrow]');
    this.title = this.querySelector('[data-plaay-title]');
    this.cta = this.querySelector('[data-plaay-cta]');
    this.ctaLabel = this.querySelector('[data-plaay-cta-label]');
    this.soundButton = this.querySelector('[data-plaay-sound]');
    this.menuButton = this.querySelector('[data-plaay-menu-button]');

    this.bindEvents();
    this.selectChapter(0, false);
  }

  disconnectedCallback() {
    this.stopTimer();
  }

  bindEvents() {
    this.buttons.forEach((button, index) => {
      button.addEventListener('click', () => this.selectChapter(index, true));
    });

    this.soundButton?.addEventListener('click', () => this.toggleSound());

    this.menu = document.querySelector('[data-plaay-menu]');

    this.menuButton?.addEventListener('click', () => {
      this.setMenuOpen(!document.body.classList.contains('plaay-menu-open'));
    });

    this.menu?.querySelectorAll('a').forEach((link) => {
      link.addEventListener('click', () => this.setMenuOpen(false));
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') this.setMenuOpen(false);
    });

    this.addEventListener('pointerenter', () => this.stopTimer());
    this.addEventListener('pointerleave', () => this.startTimer());

    this.addEventListener('touchstart', (event) => {
      this.touchStartX = event.changedTouches[0].clientX;
    }, { passive: true });

    this.addEventListener('touchend', (event) => {
      const delta = event.changedTouches[0].clientX - this.touchStartX;
      if (Math.abs(delta) > 45) {
        this.selectChapter(
          this.activeIndex + (delta < 0 ? 1 : -1),
          true
        );
      }
    }, { passive: true });

    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        this.stopTimer();
        this.pauseAllVideos();
      } else {
        this.playActiveVideo();
        this.startTimer();
      }
    });
  }

  setMenuOpen(open) {
    document.body.classList.toggle('plaay-menu-open', open);

    this.menuButton?.setAttribute('aria-expanded', String(open));
    this.menuButton?.setAttribute(
      'aria-label',
      open ? 'Close menu' : (this.menuButton?.dataset.menuLabel || 'Open menu')
    );

    this.menu?.setAttribute('aria-hidden', String(!open));

    if (open) {
      this.stopTimer();
      this.pauseAllVideos();
    } else {
      this.playActiveVideo();
      this.startTimer();
    }
  }

  normalizeIndex(index) {
    const count = this.buttons.length;
    return ((index % count) + count) % count;
  }

  selectChapter(index, userInitiated) {
    if (!this.buttons.length) return;

    this.activeIndex = this.normalizeIndex(index);
    const activeButton = this.buttons[this.activeIndex];

    this.buttons.forEach((button, buttonIndex) => {
      const isActive = buttonIndex === this.activeIndex;
      button.classList.toggle('is-active', isActive);
      button.setAttribute('aria-selected', String(isActive));

      if (isActive) {
        const fill = button.querySelector('.plaay-hero__progress-fill');
        if (fill) {
          fill.style.animation = 'none';
          void fill.offsetWidth;
          fill.style.animation = '';
        }
      }
    });

    this.slides.forEach((slide, slideIndex) => {
      slide.classList.toggle('is-active', slideIndex === this.activeIndex);
    });

    this.updateCopy(activeButton);
    this.playActiveVideo();
    this.startTimer();

    if (userInitiated) {
      activeButton.scrollIntoView({
        behavior: 'smooth',
        inline: 'center',
        block: 'nearest'
      });
    }
  }

  updateCopy(button) {
    if (!button) return;

    this.content?.classList.remove('is-changing');
    void this.content?.offsetWidth;

    if (this.eyebrow) {
      this.eyebrow.textContent = button.dataset.eyebrow || '';
    }

    if (this.title) {
      this.title.innerHTML = (button.dataset.title || '')
        .replace(/\n/g, '<br>');
    }

    if (this.ctaLabel) {
      this.ctaLabel.textContent = button.dataset.buttonLabel || '';
    }

    if (this.cta) {
      this.cta.href = button.dataset.buttonLink || '#';
      this.cta.hidden = !button.dataset.buttonLabel;
    }

    this.content?.classList.add('is-changing');
  }

  getVideos() {
    return [...this.querySelectorAll('video')];
  }

  pauseAllVideos() {
    this.getVideos().forEach((video) => video.pause());
  }

  playActiveVideo() {
    this.getVideos().forEach((video) => {
      const slide = video.closest('[data-plaay-slide]');
      const isActive = slide?.classList.contains('is-active');

      video.muted = !this.soundOn;

      if (isActive) {
        video.play().catch(() => {});
      } else {
        video.pause();
        try {
          video.currentTime = 0;
        } catch (error) {
          // Ignore browsers that block seeking before metadata loads.
        }
      }
    });
  }

  toggleSound() {
    this.soundOn = !this.soundOn;

    this.getVideos().forEach((video) => {
      video.muted = !this.soundOn;
    });

    this.soundButton?.setAttribute('aria-pressed', String(this.soundOn));

    if (this.soundButton) {
      this.soundButton.textContent = this.soundOn
        ? this.config.soundOnLabel
        : this.config.soundOffLabel;
    }
  }

  startTimer() {
    this.stopTimer();

    if (!this.autoplayEnabled || this.buttons.length < 2) return;

    this.timer = window.setTimeout(() => {
      this.selectChapter(this.activeIndex + 1, false);
    }, this.duration);
  }

  stopTimer() {
    if (this.timer) {
      window.clearTimeout(this.timer);
      this.timer = null;
    }
  }
}

if (!customElements.get('plaay-hero')) {
  customElements.define('plaay-hero', PlaayHero);
}

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('[data-plaay-hero]').forEach((element) => {
    if (!element.matches('plaay-hero')) {
      Object.setPrototypeOf(element, PlaayHero.prototype);
      element.connectedCallback();
    }
  });
});
